<script>
import ValidationStatusColumnStatusTooltip
  from '@/components/document-validation/documentValidationDataTable/ValidationStatusColumnStatusTooltip.vue'
import documentValidationDataTableJson from '@/data/json/document-validation/documentValidationDataTable.json'
import DocumentsTableActions from '@/components/documents/DocumentsTableActions.vue'
import { getDocumentTableHeaders } from '@/components/documents/DocumentTableHeaders'
import { DeliveryType, DocumentProductionStatus } from '@/enums'
import { mapGetters } from 'vuex'
import RecipientAddressBlock
  from "@/components/document-validation/documentValidationDataTable/RecipientAddressBlock.vue";

export default {
  components: {
    RecipientAddressBlock,
    DocumentsTableActions,
    ValidationStatusColumnStatusTooltip,
  },
  props: {
    order: { type: [Object, Array] }, // comes from documentValidation as Object, ordersWrapper as Array
    visibleDocumentStatus: { type: Object },
    visibleCostCenter: { type: Array },
    height: { type: Number, default: 540 },
    fetchOrders: { type: Function }, // from OrdersWrapper
    showOrderNumber: { type: Boolean, default: false },
    filterRules: { type: Object }
  },
  data: (vm) => ({
    translations: documentValidationDataTableJson,
    validationResults: [],
    validationItem: null,
    pollingPendingDocuments: null,
    pollingPendingDocumentsInterval: 5000, // in milliseconds
    isLoading: true,
    isCalling: false,
    isDeleting: false,
    showOrganization: (vm.$store.getters['organizations'] || []).length > 1,
    deletedCount: 0,
    deletedOrderIds: [],
    orderExt: [], // extended Order
    selected: [], // selected items
    search: '', // the search text
    singleExpand: false, // single or multi expandable rows
    expanded: [], // the expanded items
    transitioned: [], // indicator for row-transitioning
    closeTimeouts: {}, // timeouts for row-expandables
    loadingDetails: [], // loading-animation indicator
    userActionRequired: {},
    menuAttrs: null
  }),
  computed: {
    ...mapGetters(['hasPermission']),
    DeliveryType() {
      return DeliveryType
    },
    // CarrierType() {
    //   return CarrierType
    // },
    DocumentProductionStatus() {
      return DocumentProductionStatus
    },
    isIterableOrder() {
      return Array.isArray(this.order)
    },
    hasPending() {
      let pending = false
      if (
        this.validationResults.find(
          x => x.validationStatus === this.$enums.validationStatus.pending
        )
      ) {
        pending = true
      } else {
        // no pending documents ? stop the interval
        this.stopPolling()
      }
      return pending
    },
    hasCostCenter() {
      return this.validationResults.find(x => x.costCenter)
    },
    isPending() {
      return item =>
        item.validationStatus === this.$enums.validationStatus.pending
    },
    computedHeaders() {
      return getDocumentTableHeaders(
        this.visibleDocumentStatus,
        this.hasCostCenter,
        this.showOrderNumber,
        this.showOrganization,
        this.visibleCostCenter,
        this.isIterableOrder,
        this.$permissions?.hasBriefbutler
      )
    }
  },
  watch: {
    validationResults(validationResultsNew, validationResultsOld) {
      // on deletion, do nothing
      if (this.isDeleting) return
      // reduce validationResult into a small array
      // Array({orderId: Integer, validationStatus: Array()})
      const reducer = (acc, d) => {
        const found = acc.find(a => a?.orderId === d?.orderId)
        const value = d.validationStatus
        if (!found) {
          acc.push({ orderId: d.orderId, validationStatus: [value] })
        } else {
          found.validationStatus.push(value)
        }
        return acc
      }

      const resultOld = validationResultsOld.reduce(reducer, [])
      const resultNew = validationResultsNew.reduce(reducer, [])

      let updatedOrderIds = []
      resultOld.forEach(oldElement => {
        // old result has pending documents
        if (
          oldElement.validationStatus.includes(
            this.$enums.validationStatus.pending
          )
        ) {
          // get all results that don't has pending documents
          if (
            !resultNew
              .find(eNew => eNew.orderId === oldElement.orderId)
              .validationStatus.includes(this.$enums.validationStatus.pending)
          )
            updatedOrderIds.push(oldElement.orderId)
        }
      })
      // are there updatedOrderIds ?
      if (updatedOrderIds.length > 0) {
        // update Orders
        this.$emit('update:updated', {
          orderIds: updatedOrderIds
        })
      }
    }
  },
  created() {
    this.orderExt = this.orderExt.concat(this.order) // make array from object
    this.getValidation().then(() => {
      if (this.hasPending) {
        this.startPolling()
      }
    })
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    getTranslationFor(prefix,key) {
      return this.$t(prefix+key)
    },
    getTypeKey(type, value) {
      for (const key in type) {
        if (type[key] === value) {
          return key
        }
      }
      return null
    },
    findOrderItemByDocumentId(orders, itemDocumentId) {
      if(this.isIterableOrder){
        for (const order of orders) {
          for (const position of order?.orderPositions) {
            for (const document of position?.documents) {
              if (document?.documentId === itemDocumentId) {
                for (const orderPosition of order?.orderPositions) {
                  for (const document of orderPosition?.documents) {
                    if (document?.documentId === itemDocumentId) {
                      return document
                    }
                  }
                }
              }
            }
          }
        }
        return null
      }
      return null
    },
    getOrderDetails() {
      let orderIds = []
      let orderNumbers = []
      this.orderExt.forEach(order => {
        orderIds.push(order.orderId)
        orderNumbers.push({ [order.orderId]: order.orderNumber })
      })
      return {
        orderNumbers: orderNumbers,
        orderIds: orderIds
      }
    },
    getValidation(polling) {
      return new Promise(resolve => {
        this.isCalling = true
        let orderDetails = this.getOrderDetails()
        this.$uploadService
          .validation({ orderIds: orderDetails.orderIds })
          .then(validationResponse => {
            this.validationResults = validationResponse.fileValidations
            this.userActionRequired = validationResponse.userActionRequired
            this.validationResults.forEach(result => {
              let orderNumber = orderDetails.orderNumbers.find(
                orderNumber => orderNumber[result.orderId]
              )
              let orderId = orderDetails.orderIds.find(
                orderId => orderId === result.orderId
              )
              let orderOwner = this.orderExt.find(
                order => order.orderId === orderId
              )
              result.orderNumber = orderNumber[result.orderId]
              result.orderId = orderId
              result.owner = orderOwner.owner || {
                firstName: '-',
                lastName: '-'
              }
            })
            // return documents to parent
            this.returnDocuments()
          })
          .catch(() => {
            this.stopPolling()
            this.validationResults = []
            this.returnDocuments()
          })
          .finally(() => {
            this.isCalling = false
            // return resolve-promise
            resolve(true)
            if (!polling) {
              // don't show loading / error messages on polling
              this.isLoading = false
            }
          })
      })
    },
    returnDocuments() {
      // return documents to parent
      this.$emit('returnDocuments', {
        validationResults: this.validationResults,
        userActionRequired: this.userActionRequired
      })
    },
    startPolling() {
      if (this.pollingPendingDocuments === null) {
        this.pollingPendingDocuments = setInterval(() => {
          // skip call when request is still calling (pending-call)
          if (!this.isCalling)
            this.getValidation(true).then(() => {
              if (!this.hasPending) {
                this.stopPolling()
                this.selected = [] // deselect all
              }
            })
        }, this.pollingPendingDocumentsInterval)
      }
    },
    stopPolling() {
      clearInterval(this.pollingPendingDocuments)
      this.pollingPendingDocuments = null
    },
    restartPolling(items) {
      // set pending-status
      items.forEach(item => {
        item.validationStatus = this.$enums.validationStatus.pending
      })
      this.returnDocuments()
      this.startPolling()
    }
  }
}
</script>
<template>
  <div>
    <v-layout v-if="isLoading" row justify-center align-center class="mt-12">
      <v-progress-circular indeterminate></v-progress-circular>
    </v-layout>
    <v-overlay :value="isDeleting">
      <v-card light>
        <v-card-text class="text-center">
          <v-progress-linear indeterminate color="red"></v-progress-linear>
          <span>{{ $t('component.documents.documentsTable.overlay.deleting.documents') }}</span>
        </v-card-text>
      </v-card>
    </v-overlay>
    <v-data-table
      v-if="!isLoading"
      id="documents-table"
      ref="vDataTable"
      v-model="selected"
      :header-props="{
        sortByText: $t('global.sort.by.text')
      }"
      :footer-props="{
        'items-per-page-text': $t('global.items.per.page.text'),
        'items-per-page-all-text': $t('global.items.per.page.text'),
        'page-text': $t('global.items.per.page.all.text'),
        'items-per-page-options': [10, 25, 50, 100, -1]
      }"
      :headers="computedHeaders"
      :items="validationResults"
      :items-per-page="25"
      :show-select="order.hasOwnProperty('orderStatus') && order.orderStatus < 400 && $documentsService.canDeleteDocuments(validationResults)"
      :height="height"
      :search="search"
      sort-by="documentId"
      item-key="documentId"
      :sort-desc="true"
      elevation="0"
      :no-data-text="$t('global.no.data.text')"
      :no-results-text="$t('global.no.data.text')"
      fixed-header
      calculate-widths>
      <template #no-data>
        {{ $t('global.no.entries') }}
      </template>
      <template #top>
        <v-text-field
          id="documents-table-search"
          v-model="search"
          :label="$t('component.documents.documentsTable.search.label')"
          class="mx-4">
        </v-text-field>
      </template>
      <template #[`item.validationStatus`]="{ item }">
        <ValidationStatusColumnStatusTooltip
          :value="item.validationStatus"
          :item="item"
          :content="translations" />
      </template>
      <template #[`item.orderNumber`]="{item}">
        <h4 class="primary--text text-no-wrap">
          {{ item.orderNumber }}
        </h4>
      </template>
      <template #[`item.organizationId`]="{item}">
        <span >{{$organizationService.getOrganizationNameById(item.organizationId)}}</span>
      </template>
      <template #[`item.uploadFileName`]="{item}">
        <div style="position:relative">
           <span >
          {{ item.uploadFileName }}
           </span>
          <v-chip
            v-if="item.mergeCount"
            x-small
            color="success"
            class="round-chip"
            style="position:absolute;top:-12px;right:-15px;">
            + {{ item.mergeCount }}
          </v-chip>
        </div>
      </template>
      <template #[`item.recipientInfo.recipient`]="{ item }">
        <span
          v-if="!isPending(item) &&
            item.recipientInfo !== null &&
            item.recipientInfo !== undefined">
              <span style="position:relative;" >
                <RecipientAddressBlock
                  :item="item.recipientInfo"
                  :is-country-visible="false"
                  :is-vertical="true" />
              </span>
        </span>
        <span v-else> - </span>
      </template>
      <template #[`item.pageCount`]="{ item }">
        <span
          v-if="!isPending(item)"
        >{{ item.pageCount }}</span>
        <span v-else>-</span>
      </template>
      <template #[`item.costCenter`]="{ item }">
        <v-chip
          v-if="item.costCenter"
          small>{{ item.costCenter }}</v-chip>
      </template>
      <!--      <template #[`item.carrier`]="{ item }">-->
      <!--        <span v-if="isIterableOrder">-->
      <!--          {{getTranslationFor('carrier_', getTypeKey(CarrierType, findOrderItemByDocumentId(order, item.documentId).carrier))}}-->
      <!--        </span>-->
      <!--      </template>-->
      <template #[`item.deliveryType`]="{ item }">
        <span v-if="isIterableOrder">
            <v-chip
              v-if="findOrderItemByDocumentId(order, item.documentId).deliveryType"
              small>
                {{getTranslationFor('component.documents.documentsTable.deliveryType.',
              getTypeKey(DeliveryType, findOrderItemByDocumentId(order, item.documentId).deliveryType))}}
            </v-chip>
        </span>
      </template>
      <template #[`item.productionStatus`]="{ item }">
        <span v-if="isIterableOrder">
          <v-chip small>
          {{getTranslationFor('component.documents.documentsTable.productionStatus.',
            getTypeKey(DocumentProductionStatus, findOrderItemByDocumentId(order, item.documentId).productionStatus))}}
            </v-chip>
        </span>
      </template>
      <template #[`item.actions`]="{ item }">
        <DocumentsTableActions
          :item="item"
          :order="order"
          :validation-results="validationResults"
          @cancelled="fetchOrders"
          @restart-polling="restartPolling" />
      </template>
    </v-data-table>
  </div>
</template>
<style>
.v-data-table .v-data-table__wrapper tbody tr {
  height: 56px;
}
.v-data-table .v-data-table__wrapper tbody td {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}
</style>
